import { useEffect, useState } from 'react';
import { Save, RefreshCw, Brain, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../components/ui/ToastSystem';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';

const COST_PER_READING = 0.12;

interface ServiceStat {
  service: string;
  count: number;
  cost: number;
}

interface AiConfig {
  services: {
    name: string;
    model: 'haiku' | 'sonnet' | 'opus';
    maxTokens: number;
    cacheEnabled: boolean;
  }[];
}

interface AiAlerts {
  maxDailyCost: number;
  minCacheHitRate: number;
  adminWhatsapp: string;
}

interface CacheStats {
  hit: number;
  miss: number;
}

const DEFAULT_AI_CONFIG: AiConfig = {
  services: [
    { name: 'birth-chart',   model: 'sonnet', maxTokens: 2000, cacheEnabled: true },
    { name: 'forecast',      model: 'haiku',  maxTokens: 1500, cacheEnabled: true },
    { name: 'tarot',         model: 'haiku',  maxTokens: 1000, cacheEnabled: false },
    { name: 'questions',     model: 'sonnet', maxTokens: 2000, cacheEnabled: true },
    { name: 'synastry',      model: 'sonnet', maxTokens: 2000, cacheEnabled: true },
  ],
};

const DEFAULT_AI_ALERTS: AiAlerts = {
  maxDailyCost: 50,
  minCacheHitRate: 50,
  adminWhatsapp: '',
};

function MetricCard({
  label, value, color, sub,
}: { label: string; value: string; color: string; sub?: string }) {
  return (
    <div className="card" style={{ textAlign: 'center' }}>
      <p style={{ fontSize: '1.5rem', fontWeight: 700, color, margin: '0 0 0.25rem' }}>{value}</p>
      {sub && <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: '0 0 0.25rem' }}>{sub}</p>}
      <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>{label}</p>
    </div>
  );
}

export function AdminAiMonitoringPage() {
  const { showToast } = useToast();

  const [loading,        setLoading]        = useState(true);
  const [todayCount,     setTodayCount]     = useState(0);
  const [monthCount,     setMonthCount]     = useState(0);
  const [cacheStats,     setCacheStats]     = useState<CacheStats | null>(null);
  const [serviceStats,   setServiceStats]   = useState<ServiceStat[]>([]);

  const [aiConfig,       setAiConfig]       = useState<AiConfig>(DEFAULT_AI_CONFIG);
  const [aiAlerts,       setAiAlerts]       = useState<AiAlerts>(DEFAULT_AI_ALERTS);
  const [savingConfig,   setSavingConfig]   = useState(false);
  const [savingAlerts,   setSavingAlerts]   = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);

    const now        = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

    const [todayRes, monthRes, servicesRes, cacheRes, configRes, alertsRes] = await Promise.all([
      supabase
        .from('reading_analytics')
        .select('id', { count: 'exact', head: true })
        .gte('created_at', todayStart),
      supabase
        .from('reading_analytics')
        .select('id', { count: 'exact', head: true })
        .gte('created_at', monthStart),
      supabase
        .from('reading_analytics')
        .select('feature')
        .gte('created_at', new Date(Date.now() - 30 * 86_400_000).toISOString()),
      supabase
        .from('platform_settings')
        .select('value')
        .eq('key', 'ai_cache_stats')
        .maybeSingle(),
      supabase
        .from('platform_settings')
        .select('value')
        .eq('key', 'ai_config')
        .maybeSingle(),
      supabase
        .from('platform_settings')
        .select('value')
        .eq('key', 'ai_alerts')
        .maybeSingle(),
    ]);

    setTodayCount(todayRes.count ?? 0);
    setMonthCount(monthRes.count ?? 0);

    // Cache stats
    if (cacheRes.data?.value) {
      try {
        const parsed = typeof cacheRes.data.value === 'string'
          ? JSON.parse(cacheRes.data.value)
          : cacheRes.data.value;
        if (parsed && typeof parsed.hit === 'number') setCacheStats(parsed as CacheStats);
      } catch { /* ignore */ }
    }

    // Service stats from reading_analytics rows
    if (servicesRes.data) {
      const map: Record<string, number> = {};
      for (const row of servicesRes.data) {
        const svc = (row.feature as string | null) ?? 'desconhecido';
        map[svc] = (map[svc] ?? 0) + 1;
      }
      const list = Object.entries(map)
        .map(([service, count]) => ({ service, count, cost: Math.round(count * COST_PER_READING * 100) / 100 }))
        .sort((a, b) => b.count - a.count);
      setServiceStats(list);
    }

    // AI config
    if (configRes.data?.value) {
      try {
        const parsed = typeof configRes.data.value === 'string'
          ? JSON.parse(configRes.data.value)
          : configRes.data.value;
        if (parsed?.services) setAiConfig(parsed as AiConfig);
      } catch { /* use default */ }
    }

    // AI alerts
    if (alertsRes.data?.value) {
      try {
        const parsed = typeof alertsRes.data.value === 'string'
          ? JSON.parse(alertsRes.data.value)
          : alertsRes.data.value;
        if (parsed) setAiAlerts({ ...DEFAULT_AI_ALERTS, ...(parsed as Partial<AiAlerts>) });
      } catch { /* use default */ }
    }

    setLoading(false);
  }

  async function saveConfig() {
    setSavingConfig(true);
    const { error } = await supabase.from('platform_settings').upsert({
      key: 'ai_config',
      value: JSON.stringify(aiConfig),
      description: 'Configuração da IA por serviço',
      updated_at: new Date().toISOString(),
    }, { onConflict: 'key' });
    setSavingConfig(false);
    if (error) showToast({ type: 'error', message: 'Erro ao salvar configurações da IA' });
    else showToast({ type: 'success', message: 'Configurações da IA salvas!' });
  }

  async function saveAlerts() {
    setSavingAlerts(true);
    const { error } = await supabase.from('platform_settings').upsert({
      key: 'ai_alerts',
      value: JSON.stringify(aiAlerts),
      description: 'Alertas configuráveis da IA',
      updated_at: new Date().toISOString(),
    }, { onConflict: 'key' });
    setSavingAlerts(false);
    if (error) showToast({ type: 'error', message: 'Erro ao salvar alertas' });
    else showToast({ type: 'success', message: 'Alertas salvos!' });
  }

  function updateServiceField<K extends keyof AiConfig['services'][number]>(
    idx: number,
    field: K,
    value: AiConfig['services'][number][K],
  ) {
    setAiConfig(prev => {
      const services = prev.services.map((s, i) => i === idx ? { ...s, [field]: value } : s);
      return { ...prev, services };
    });
  }

  // Derived metrics
  const todayCost  = Math.round(todayCount  * COST_PER_READING * 100) / 100;
  const monthCost  = Math.round(monthCount  * COST_PER_READING * 100) / 100;
  const cacheTotal = cacheStats ? cacheStats.hit + cacheStats.miss : 0;
  const cacheRate  = cacheTotal > 0 ? Math.round((cacheStats!.hit / cacheTotal) * 100) : null;
  const cacheColor = cacheRate === null ? 'var(--text-muted)' : cacheRate >= 70 ? '#4ade80' : cacheRate >= 50 ? '#fbbf24' : '#f87171';

  return (
    <div>
      <AdminPageHeader
        title="Monitoramento de IA"
        objective="Acompanhe os custos de IA em tempo real, o cache hit rate, as leituras por serviço e ajuste as configurações e alertas sem precisar de deploy."
      />

      {/* Section 1, Live metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        <MetricCard
          label="Custo do dia (R$)"
          value={`R$ ${todayCost.toFixed(2)}`}
          color="#fbbf24"
          sub={`${todayCount} leituras`}
        />
        <MetricCard
          label="Custo do mês (R$)"
          value={`R$ ${monthCost.toFixed(2)}`}
          color="#f59e0b"
          sub={`${monthCount} leituras`}
        />
        <MetricCard
          label="Cache hit rate"
          value={cacheRate !== null ? `${cacheRate}%` : '-'}
          color={cacheColor}
          sub={cacheTotal > 0 ? `${cacheStats!.hit} hits / ${cacheTotal} total` : 'Sem dados'}
        />
        <MetricCard
          label="Total leituras hoje"
          value={todayCount.toLocaleString('pt-BR')}
          color="#60a5fa"
        />
        <MetricCard
          label="Total leituras do mês"
          value={monthCount.toLocaleString('pt-BR')}
          color="#818cf8"
        />
      </div>

      {loading ? (
        <div className="card shimmer" style={{ height: '300px' }} />
      ) : (
        <>
          {/* Section 2, Uso por Serviço */}
          <div style={{ marginBottom: '2rem' }}>
            <p style={{ fontSize: 'var(--text-caption)', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.75rem' }}>
              Uso por Serviço (últimos 30 dias)
            </p>
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Serviço</th>
                    <th style={{ textAlign: 'right' }}>Leituras (30d)</th>
                    <th style={{ textAlign: 'right' }}>Custo estimado (R$)</th>
                  </tr>
                </thead>
                <tbody>
                  {serviceStats.length === 0 ? (
                    <tr>
                      <td colSpan={3} style={{ textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', padding: '2rem' }}>
                        Nenhum dado encontrado nos últimos 30 dias.
                      </td>
                    </tr>
                  ) : (
                    serviceStats.map(s => (
                      <tr key={s.service}>
                        <td style={{ color: 'var(--text-primary)', fontWeight: 600, fontFamily: 'monospace', fontSize: '12px' }}>{s.service}</td>
                        <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{s.count.toLocaleString('pt-BR')}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: '#fbbf24' }}>R$ {s.cost.toFixed(2)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Section 3, Configurações da IA */}
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
              <p style={{ fontSize: 'var(--text-caption)', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>
                Configurações da IA
              </p>
              <button className="btn btn-primary btn-sm" onClick={saveConfig} disabled={savingConfig} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                <Save size={13} /> {savingConfig ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Serviço</th>
                    <th>Modelo padrão</th>
                    <th style={{ textAlign: 'right' }}>Max tokens</th>
                    <th style={{ textAlign: 'center' }}>Cache ativo</th>
                  </tr>
                </thead>
                <tbody>
                  {aiConfig.services.map((svc, idx) => (
                    <tr key={svc.name}>
                      <td style={{ color: 'var(--text-primary)', fontWeight: 600, fontFamily: 'monospace', fontSize: '12px' }}>{svc.name}</td>
                      <td>
                        <select
                          className="form-input"
                          style={{ fontSize: '12px', padding: '2px 6px' }}
                          value={svc.model}
                          onChange={e => updateServiceField(idx, 'model', e.target.value as 'haiku' | 'sonnet' | 'opus')}
                        >
                          <option value="haiku">haiku</option>
                          <option value="sonnet">sonnet</option>
                          <option value="opus">opus</option>
                        </select>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <input
                          type="number"
                          className="form-input"
                          style={{ fontSize: '12px', padding: '2px 6px', width: '90px', textAlign: 'right' }}
                          value={svc.maxTokens}
                          min={100}
                          max={8000}
                          step={100}
                          onChange={e => updateServiceField(idx, 'maxTokens', parseInt(e.target.value) || svc.maxTokens)}
                        />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={svc.cacheEnabled}
                          onChange={e => updateServiceField(idx, 'cacheEnabled', e.target.checked)}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Section 4, Alertas configuráveis */}
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
              <p style={{ fontSize: 'var(--text-caption)', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <AlertCircle size={13} /> Alertas configuráveis
              </p>
              <button className="btn btn-primary btn-sm" onClick={saveAlerts} disabled={savingAlerts} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                <Save size={13} /> {savingAlerts ? 'Salvando...' : 'Salvar alertas'}
              </button>
            </div>
            <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
                <div>
                  <label style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', display: 'block', marginBottom: '0.3rem' }}>
                    Custo diário máximo (R$)
                  </label>
                  <input
                    type="number"
                    className="form-input"
                    value={aiAlerts.maxDailyCost}
                    min={0}
                    step={1}
                    onChange={e => setAiAlerts(prev => ({ ...prev, maxDailyCost: parseFloat(e.target.value) || 0 }))}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', display: 'block', marginBottom: '0.3rem' }}>
                    Cache hit rate mínimo (%)
                  </label>
                  <input
                    type="number"
                    className="form-input"
                    value={aiAlerts.minCacheHitRate}
                    min={0}
                    max={100}
                    step={1}
                    onChange={e => setAiAlerts(prev => ({ ...prev, minCacheHitRate: parseInt(e.target.value) || 0 }))}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', display: 'block', marginBottom: '0.3rem' }}>
                    WhatsApp do admin para alertas
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="+55 11 99999-9999"
                    value={aiAlerts.adminWhatsapp}
                    onChange={e => setAiAlerts(prev => ({ ...prev, adminWhatsapp: e.target.value }))}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Reload */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost btn-sm" onClick={load} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <RefreshCw size={13} /> Recarregar dados
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// Export icon for sidebar / other pages
export { Brain as AiMonitoringIcon };
